import React, { useState, useEffect } from 'react';

const PWAInstallButton = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [platform, setPlatform] = useState(null);
  const [isStandalone, setIsStandalone] = useState(false);
  const [showButton, setShowButton] = useState(false);
  const [showAlways, setShowAlways] = useState(false); // New state

  useEffect(() => {
    // Check if already installed
    const checkIfInstalled = () => {
      return (
        window.matchMedia('(display-mode: standalone)').matches ||
        window.navigator.standalone ||
        document.referrer.includes('android-app://')
      );
    };

    const installed = checkIfInstalled();
    setIsStandalone(installed);

    // Detect platform
    const detectPlatform = () => {
      const ua = navigator.userAgent.toLowerCase();
      
      if (/iphone|ipad|ipod/.test(ua)) return 'ios';
      if (/android/.test(ua)) return 'android';
      if (/chrome/.test(ua)) return 'chrome';
      if (/edg/.test(ua)) return 'edge';
      if (/firefox/.test(ua)) return 'firefox';
      if (/safari/.test(ua) && !/chrome/.test(ua)) return 'safari';
      return 'other';
    };

    const detectedPlatform = detectPlatform();
    setPlatform(detectedPlatform);

    // Handle install prompt
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowButton(true);
    };

    // Check localStorage for user preference
    const alwaysShow = localStorage.getItem('pwaAlwaysShow') === 'true';
    setShowAlways(alwaysShow);

    // Show button based on conditions
    const shouldShowButton = () => {
      if (installed) return false;
      
      // Always show if user selected "always show"
      if (alwaysShow) return true;
      
      // Show for supported platforms
      const supportedPlatforms = ['ios', 'android', 'chrome', 'edge', 'firefox'];
      return supportedPlatforms.includes(detectedPlatform);
    };

    setShowButton(shouldShowButton());

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Cleanup
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      try {
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        
        if (outcome === 'accepted') {
          console.log('User accepted the install prompt');
          setDeferredPrompt(null);
          setShowButton(false);
        }
      } catch (error) {
        console.error('Error during install:', error);
        showInstructions();
      }
    } else {
      showInstructions();
    }
  };

  const showInstructions = () => {
    let message = '';
    
    switch(platform) {
      case 'ios':
        message = 'To install on iOS:\n1. Tap Share button (📤)\n2. Scroll to "Add to Home Screen"\n3. Tap "Add"';
        break;
      case 'android':
        message = 'To install on Android:\n1. Tap menu (⋮)\n2. Tap "Install app" or "Add to Home screen"\n3. Tap "Install"';
        break;
      case 'chrome':
      case 'edge':
        message = 'To install on desktop:\n1. Look for the install icon (⎙) in the address bar\n2. Click "Install"';
        break;
      default:
        message = 'To install this app:\nCheck your browser menu for "Install" or "Add to Home Screen" option';
    }
    
    alert(message);
  };

  const toggleAlwaysShow = () => {
    const newValue = !showAlways;
    setShowAlways(newValue);
    localStorage.setItem('pwaAlwaysShow', newValue.toString());
    setShowButton(newValue || deferredPrompt || platform === 'ios');
  };

  // Don't show if already installed
  if (isStandalone) return null;
  
  // Only show if conditions are met
  if (!showButton) return null;

  const getButtonText = () => {
    if (platform === 'ios') return 'Add to Home Screen';
    if (platform === 'android' && deferredPrompt) return 'Install App';
    if (platform === 'android') return 'Install App';
    if (['chrome', 'edge', 'firefox'].includes(platform)) return 'Install App';
    return 'Install';
  };

  const getIcon = () => {
    if (platform === 'ios') return 'bi-apple';
    if (platform === 'android') return 'bi-android2';
    if (platform === 'chrome') return 'bi-google';
    if (platform === 'edge') return 'bi-microsoft';
    if (platform === 'firefox') return 'bi-browser-firefox';
    return 'bi-download';
  };

  return (
    <div className="pwa-install-container">
      <div className="d-flex align-items-center gap-2">
        <button
          className="btn btn-primary pwa-install-btn"
          onClick={handleInstall}
          style={{
            padding: '8px 16px',
            fontSize: '14px',
            borderRadius: '6px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}
        >
          <i className={`bi ${getIcon()}`}></i>
          {getButtonText()}
        </button>
        
        {/* Settings dropdown for advanced users */}
        <div className="dropdown">
          <button 
            className="btn btn-outline-secondary btn-sm dropdown-toggle" 
            type="button" 
            data-bs-toggle="dropdown"
            style={{
              padding: '4px 8px',
              fontSize: '12px'
            }}
          >
            <i className="bi bi-gear"></i>
          </button>
          <ul className="dropdown-menu dropdown-menu-end">
            <li>
              <div className="dropdown-item">
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={showAlways}
                    onChange={toggleAlwaysShow}
                    id="alwaysShowPWA"
                  />
                  <label className="form-check-label" htmlFor="alwaysShowPWA">
                    Always show install button
                  </label>
                </div>
              </div>
            </li>
            <li><hr className="dropdown-divider" /></li>
            <li>
              <button 
                className="dropdown-item"
                onClick={showInstructions}
              >
                <i className="bi bi-info-circle me-2"></i>
                Installation instructions
              </button>
            </li>
          </ul>
        </div>
      </div>
      
      {!deferredPrompt && platform && (
        <small className="text-muted d-block mt-1" style={{ fontSize: '11px' }}>
          {platform === 'ios' ? 'Safari browser' : 
           platform === 'android' ? 'Mobile browser' : 
           'Desktop browser'}
        </small>
      )}
    </div>
  );
};

export default PWAInstallButton;